import { useEffect, useRef, useState } from 'react'
import { cls } from '../util'
import { CheckIcon, ChevronDownIcon } from '../icons'

/*
  The client's one place for the look: the project's look (shared, when the team lets them pick) and
  Clean or Filmic (Film Emulation's grain and halation on top). Both are saved with the project, so
  everyone sees the same grade. Plain Rec.709 has no film, so it has no Clean or Filmic either.
*/
export type LookChoice = {
  value: string; label: string | null; film: boolean; filmic?: boolean; options: { key: string; label: string; note?: string }[]
  onChange?: (key: string) => void; onFilm?: (on: boolean) => void
}

export function LookMenu({ look }: { look: LookChoice }) {
  const film = look.film
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const off = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false)
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('mousedown', off)
    window.addEventListener('keydown', esc)
    return () => {
      window.removeEventListener('mousedown', off)
      window.removeEventListener('keydown', esc)
    }
  }, [open])
  const pick = (key: string) => {
    look.onChange?.(key)
    setOpen(false)
  }
  return (
    <div className="look-menu" ref={ref}>
      <span className="look-label" aria-hidden="true">Look</span>
      <button className={cls('ctl-look', look.value && 'on')} onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Look" title="Look">
        <span>{look.label || 'Look'}</span>
        {look.value && film && <span className="ctl-look-film">+ filmic</span>}
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="look-pop" role="menu" aria-label="Looks">
          {look.filmic !== false && (
            <div className="look-mode" role="group" aria-label="Texture">
              <button className={cls(!film && 'on')} onClick={() => look.onFilm?.(false)} aria-pressed={!film} disabled={!look.value || !look.onFilm}>Clean</button>
              <button className={cls(film && 'on')} onClick={() => look.onFilm?.(true)} aria-pressed={film} disabled={!look.value || !look.onFilm} title="Film grain and halation">Filmic</button>
            </div>
          )}
          {look.onChange ? (
            <div className="look-list">
              <button role="menuitemradio" aria-checked={!look.value} className={cls(!look.value && 'on')} onClick={() => pick('')}>
                <b>As shot</b><span>The camera's flat log, no look</span>{!look.value && <CheckIcon />}
              </button>
              {look.options.map((o) => (
                <button key={o.key} role="menuitemradio" aria-checked={look.value === o.key} className={cls(look.value === o.key && 'on')} onClick={() => pick(o.key)}>
                  <b>{o.label}</b>{o.note && <span>{o.note}</span>}{look.value === o.key && <CheckIcon />}
                </button>
              ))}
            </div>
          ) : (
            <p className="look-fixed">The Osmo team set this look.</p>
          )}
          <p className="look-foot">A color preview. The final grade comes later.</p>
        </div>
      )}
    </div>
  )
}
